/** @jsxImportSource @emotion/react */
import { css, CSSObject } from "@emotion/react";

import { ReactNode, SyntheticEvent } from "react";

import MuiDialog, { DialogProps as MuiDialogProps } from "@mui/material/Dialog";
import Typography from "@mui/material/Typography";

import { CloseIcon, colors } from "@dotnet/telematics-design-system";

import { useMediaQueries } from "../../shared";
import Button, {
  BUTTON_COLORS,
  BUTTON_SIZES,
  BUTTON_VARIANTS
} from "../Button/Button";
import Separator from "../Separator/Separator";
import { useStyles } from "./styles";
import { DialogWidth } from "./types/enums";

interface DialogProps {
  title: string | ReactNode | Element;
  actions: any;
  children?: any;
  description?: string | ReactNode;
  isTitleSeparator?: boolean;
  isActionsSeparator?: boolean;
  extendToMaxWidth?: boolean;
  close: any;
  submit?: () => void;
  width?: string | number;
  noValidate?: boolean;
  customPadding?: string;
  maxHeight?: string;
  wordBreakTitle?: string;
  customContentOverflow?: any;
  showXButton?: boolean;
  customChildrenStyle?: CSSObject;
  customPaperMargin?: string;
  titleImage?: React.JSX.Element;
  titleTooltip?: React.JSX.Element;
}

export type CombinedDialogProps = DialogProps & MuiDialogProps;

const Dialog = ({
  title,
  actions,
  children,
  description,
  isTitleSeparator,
  isActionsSeparator,
  extendToMaxWidth,
  close,
  submit,
  width,
  noValidate,
  customPadding,
  maxHeight,
  wordBreakTitle,
  customContentOverflow,
  showXButton = true,
  customChildrenStyle,
  customPaperMargin,
  titleImage,
  titleTooltip,
  ...props
}: CombinedDialogProps) => {
  const { toMd, fromMd } = useMediaQueries();

  const { separator, actionButtons } = useStyles({});

  const handleSubmit = (e: SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
    submit?.();
  };

  const dialogWidth = width ?? "unset";
  const maxDialogWidthFromMd = width ?? DialogWidth.MaxWidth;

  return (
    <MuiDialog
      {...props}
      id="muiDialog"
      onClose={close}
      fullScreen={toMd}
      sx={{
        "& .MuiDialog-paper": {
          width: extendToMaxWidth ? "100%" : dialogWidth,
          maxWidth: toMd ? "unset" : maxDialogWidthFromMd,
          minWidth: toMd ? "unset" : DialogWidth.MinWidth,
          borderRadius: toMd ? "0" : "12px",
          overflowY: "hidden",
          maxHeight: toMd ? "unset" : maxHeight,
          margin: fromMd ? customPaperMargin : undefined
        }
      }}
    >
      {close && showXButton && (
        <Button
          variant={BUTTON_VARIANTS.IconOnly}
          size={BUTTON_SIZES.Small}
          color={BUTTON_COLORS.White}
          icon={
            <CloseIcon onClick={close} data-testid="dialog-corner-close-btn" />
          }
          css={css({
            position: "absolute",
            top: "4px",
            right: "4px"
          })}
        />
      )}
      <div
        css={css({
          padding: customPadding ?? "24px 24px 16px 24px"
        })}
      >
        {titleImage}
        <div
          css={css({
            display: "flex",
            alignItems: "center"
          })}
        >
          <Typography
            variant="h5"
            sx={{
              marginRight: titleTooltip ? "8px" : "32px",
              display: "block",
              color: colors.textDark,
              wordBreak: wordBreakTitle ?? "normal"
            }}
          >
            {title}
          </Typography>

          {titleTooltip}
        </div>

        {description && (
          <Typography
            variant="body2Medium"
            sx={{
              marginTop: "8px",
              display: "block",
              color: colors.textDark,
              whiteSpace: "pre-line"
            }}
          >
            {description}
          </Typography>
        )}
      </div>
      {isTitleSeparator && <Separator css={css(separator)} />}

      <form
        onSubmit={handleSubmit}
        noValidate={noValidate}
        css={css({
          display: "flex",
          flexDirection: "column",
          overflowY: "hidden",
          height: "100%"
        })}
      >
        {children && (
          <div
            css={css([
              {
                overflowY: customContentOverflow ?? "auto",
                padding: customPadding ? "16px" : "16px 24px",
                height: "calc(100% - 73px)"
              },
              customChildrenStyle
            ])}
          >
            {children}
          </div>
        )}

        {isActionsSeparator && <Separator css={css(separator)} />}

        <div css={css(actionButtons as CSSObject)}>{actions}</div>
      </form>
    </MuiDialog>
  );
};

export default Dialog;
